import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Bot, CheckCircle2, ExternalLink, Hand, MessagesSquare, UserCheck } from 'lucide-react';
import {
  useTicket,
  useTicketCounts,
  useTicketsInfinite,
  useUpdateTicket,
  type TicketFilters,
} from '@/api/tickets';
import { useAllUsers } from '@/api/users';
import type { Ticket, TicketPriority } from '@/api/types';
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Spinner,
  useToast,
  ListSkeleton,
} from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { cn } from '@/lib/cn';
import { formatRelative } from '@/lib/format';
import { Conversation } from '../tickets/Conversation';
import { VerifiedBadge } from '../customers/KollabrProfile';
import { PriorityBadge, StatusBadge } from '../tickets/TicketBadges';

type Scope = 'all' | 'me' | 'unassigned' | 'human';

/** Chats the AI could not handle (handed over, failed, or no AI) carry this tag. */
const AI_HANDOFF_TAG = 'ai-handoff';

const PRIORITY_RANK: Record<TicketPriority, number> = { urgent: 0, high: 1, normal: 2, low: 3 };
const PRIORITY_STYLE: Record<TicketPriority, { label: string; className: string }> = {
  urgent: { label: 'Urgent', className: 'bg-red-50 text-red-700 ring-red-200' },
  high: { label: 'High', className: 'bg-amber-50 text-amber-700 ring-amber-200' },
  normal: { label: 'Medium', className: 'bg-sky-50 text-sky-700 ring-sky-200' },
  low: { label: 'Low', className: 'bg-slate-50 text-slate-400 ring-slate-200' },
};

const TABS: { value: Scope; label: string }[] = [
  { value: 'all', label: 'All active' },
  { value: 'human', label: 'Needs you' },
  { value: 'me', label: 'Mine' },
  { value: 'unassigned', label: 'Waiting' },
];

export function ChatsPage() {
  const { ticketId } = useParams();
  const [scope, setScope] = useState<Scope>('all');
  const counts = useTicketCounts();
  const filters: TicketFilters = {
    channel: 'chat',
    status: 'open,pending',
    ...(scope === 'human' ? { tag: AI_HANDOFF_TAG } : {}),
    assignee: scope === 'all' || scope === 'human' ? undefined : scope,
  };
  const chats = useTicketsInfinite(filters);
  const loaded = chats.data?.pages.flatMap((p) => p.data) ?? [];
  // Chats waiting for a person: most urgent first, then the longest-waiting.
  const rows =
    scope === 'human'
      ? [...loaded].sort(
          (a, b) =>
            PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] ||
            new Date(a.lastMessageAt).getTime() - new Date(b.lastMessageAt).getTime(),
        )
      : loaded;
  const needsHuman = counts.data?.needsHuman ?? 0;

  return (
    <div className="flex flex-col gap-4 lg:h-[calc(100dvh-7rem)]">
      <div className={cn(ticketId && 'hidden lg:block')}>
        <p className="text-brand-600 mb-1 text-xs font-semibold tracking-wider uppercase">Support</p>
        <h1 className="text-2xl font-bold sm:text-[28px] sm:leading-9">Live chats</h1>
        <p className="mt-1 text-sm text-slate-500">
          Active conversations from the website widget and app. Updates live.
        </p>
      </div>

      <Card className="flex min-h-[32rem] flex-1 overflow-hidden">
        <aside
          aria-label="Active chats"
          className={cn(
            'flex w-full flex-col border-r border-slate-200 lg:w-80 lg:shrink-0',
            ticketId && 'hidden lg:flex',
          )}
        >
          <div role="tablist" aria-label="Filter chats" className="flex gap-1 border-b border-slate-100 p-2">
            {TABS.map(({ value: s, label }) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={scope === s}
                onClick={() => setScope(s)}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1 rounded-md px-1.5 py-1.5 text-xs font-semibold whitespace-nowrap',
                  scope === s ? 'bg-brand-50 text-brand-700' : 'text-slate-500 hover:bg-slate-100',
                )}
              >
                {label}
                {s === 'human' && needsHuman > 0 && (
                  <span className="rounded-full bg-red-500 px-1.5 text-[10px] leading-4 font-bold text-white tabular-nums">
                    {needsHuman}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {chats.isPending ? (
              <ListSkeleton rows={5} label="Loading chats" />
            ) : chats.error ? (
              <ErrorState error={chats.error} onRetry={() => void chats.refetch()} />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<MessagesSquare className="h-6 w-6" />}
                title={scope === 'human' ? 'Nothing needs you' : 'No active chats'}
                description={
                  scope === 'human'
                    ? 'Chats the AI can’t handle land here, most urgent first.'
                    : 'New conversations appear here automatically.'
                }
              />
            ) : (
              <ul className="divide-y divide-slate-100">
                {rows.map((c) => (
                  <ChatRow key={c.id} chat={c} active={c.id === ticketId} />
                ))}
              </ul>
            )}
          </div>
        </aside>

        <section className={cn('min-w-0 flex-1 flex-col', ticketId ? 'flex' : 'hidden lg:flex')}>
          {ticketId ? (
            <ChatPanel ticketId={ticketId} />
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <EmptyState
                icon={<MessagesSquare className="h-6 w-6" />}
                title="Pick a conversation"
                description="Select a chat on the left to reply."
              />
            </div>
          )}
        </section>
      </Card>
    </div>
  );
}

function ChatRow({ chat, active }: { chat: Ticket; active: boolean }) {
  const users = useAllUsers();
  const assignee = chat.assigneeId ? users.data?.find((u) => u.id === chat.assigneeId) : undefined;
  const waiting = chat.lastMessageAuthorType === 'customer';
  const name = chat.customer?.name ?? 'Visitor';
  return (
    <li>
      <Link
        to={`/chats/${chat.id}`}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex gap-3 px-3 py-3 transition-colors',
          active ? 'bg-brand-50/70 shadow-[inset_3px_0_0_var(--color-brand-500)]' : 'hover:bg-slate-50',
        )}
      >
        <span className="relative">
          <Avatar name={name} src={chat.customer?.avatarUrl} size="sm" />
          {waiting && (
            <span
              className="bg-accent-500 absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white"
              aria-label="Waiting for reply"
            />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span
              className={cn('truncate text-sm', waiting ? 'font-semibold' : 'font-medium text-slate-700')}
            >
              {name} <VerifiedBadge at={chat.customer?.identityVerifiedAt} compact />
            </span>
            <span className="ml-auto shrink-0 text-[11px] text-slate-400">
              {formatRelative(chat.lastMessageAt)}
            </span>
          </div>
          <p className={cn('truncate text-sm', waiting ? 'text-slate-800' : 'text-slate-500')}>
            {chat.lastMessagePreview}
          </p>
          <div className="mt-1 flex min-w-0 items-center gap-1.5 text-[11px]">
            <PriorityChip priority={chat.priority} />
            <Handler chat={chat} assigneeName={assignee?.name ?? null} />
            <span className="truncate text-slate-400">{chat.subject}</span>
          </div>
        </div>
      </Link>
    </li>
  );
}

function PriorityChip({ priority }: { priority: TicketPriority }) {
  const p = PRIORITY_STYLE[priority];
  return (
    <span className={cn('shrink-0 rounded px-1.5 py-px font-semibold ring-1', p.className)}>{p.label}</span>
  );
}

/** Who has the chat: a teammate, the AI, or nobody yet because the AI handed it over. */
function Handler({ chat, assigneeName }: { chat: Ticket; assigneeName: string | null }) {
  const handedOver = chat.tags.includes(AI_HANDOFF_TAG);
  if (assigneeName)
    return (
      <span className="flex shrink-0 items-center gap-0.5 font-medium text-slate-600">
        <UserCheck className="h-3 w-3" /> {assigneeName}
      </span>
    );
  if (handedOver)
    return (
      <span className="flex shrink-0 items-center gap-0.5 font-semibold text-red-600">
        <Hand className="h-3 w-3" /> Needs a person
      </span>
    );
  return (
    <span className="text-brand-600 flex shrink-0 items-center gap-0.5 font-medium">
      <Bot className="h-3 w-3" /> AI answering
    </span>
  );
}

function ChatPanel({ ticketId }: { ticketId: string }) {
  const { user, can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const chat = useTicket(ticketId);
  const update = useUpdateTicket();

  if (chat.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size="lg" label="Loading chat" />
      </div>
    );
  }
  if (chat.error) return <ErrorState error={chat.error} onRetry={() => void chat.refetch()} />;
  const t = chat.data;
  const name = t.customer?.name ?? 'Visitor';
  const ended = t.status === 'solved' || t.status === 'closed';

  return (
    <>
      <header className="flex items-center gap-3 border-b border-slate-200 px-3 py-2.5 sm:px-4">
        <Link
          to="/chats"
          className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 lg:hidden"
          aria-label="Back to chats"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <Avatar name={name} src={t.customer?.avatarUrl} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{name}</p>
          <p className="flex items-center gap-1.5 truncate text-xs text-slate-500">
            #{t.number} · {t.subject}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="hidden sm:flex sm:gap-1.5">
            <StatusBadge status={t.status} />
            <PriorityBadge priority={t.priority} />
          </span>
          {can('update', 'Ticket') && t.assigneeId !== user.id && !ended && (
            <Button
              size="sm"
              variant="secondary"
              loading={update.isPending}
              onClick={() =>
                update.mutate(
                  { id: t.id, input: { assigneeId: user.id } },
                  {
                    onSuccess: () => toast.success('Chat assigned to you'),
                    onError: (e) => toast.error(e.message),
                  },
                )
              }
            >
              {t.assigneeId ? 'Assign to me' : 'Take chat'}
            </Button>
          )}
          {can('update', 'Ticket') && !ended && (
            <Button
              size="sm"
              loading={update.isPending}
              onClick={() =>
                update.mutate(
                  { id: t.id, input: { status: 'solved' } },
                  {
                    onSuccess: () => (toast.success('Chat ended and marked solved'), navigate('/chats')),
                    onError: (e) => toast.error(e.message),
                  },
                )
              }
            >
              <CheckCircle2 className="h-4 w-4" /> <span className="hidden sm:inline">End chat</span>
            </Button>
          )}
          <Link
            to={`/inbox/${t.id}`}
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
            title="Open as ticket"
            aria-label="Open as ticket"
          >
            <ExternalLink className="h-4 w-4" />
          </Link>
        </div>
      </header>
      {t.tags.includes(AI_HANDOFF_TAG) && !ended && (
        <p className="flex items-center gap-2 border-b border-red-100 bg-red-50/70 px-4 py-2 text-xs text-red-800">
          <Hand className="h-3.5 w-3.5 shrink-0" />
          <span>
            The AI couldn’t handle this chat and handed it to your team
            {t.assigneeId === user.id ? ' — it’s assigned to you' : ''}. Its notes on what it checked are in
            the conversation.
          </span>
          <span className="ml-auto">
            <PriorityChip priority={t.priority} />
          </span>
        </p>
      )}
      <div className="dot-canvas flex min-h-0 flex-1 flex-col">
        <Conversation key={t.id} ticket={t} variant="chat" />
      </div>
    </>
  );
}
